import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  ReceiptLong as ReceiptLongIcon,
  Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon, 
  TableChart as ExcelIcon
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { reportService } from '../../services/reportServices';
import { formatCurrency, formatNumber } from '../../utils/formatNumber';
import dayjs from 'dayjs';
import BalanceReportContent from './components/BalanceReportContent';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import MissingPaymentReport from './components/MissingPaymentReport';


// 👈 DEFINIR LA INTERFAZ DE FILTROS
interface FilterFormData {
  year: number;
  months: number;
  grade: string;
  school_year_id: string;
}

//const grades = ['1A', '1B', '2', '3', '4', '5', '6&7', '8&9', '10', 'Prescolar'];

// 👈 DEFINIR LAS PESTAÑAS
interface ReportTab {
  id: string;
  label: string;
  icon: SvgIconComponent;
}

const reportTabs: ReportTab[] = [
  { id: 'balance', label: 'Balance Mensual', icon: AccountBalanceIcon },
  { id: 'morosos', label: 'Estudiantes Morosos', icon: WarningIcon },
  { id: 'ingresos', label: 'Ingresos vs Gastos', icon: TrendingUpIcon },
  { id: 'donantes', label: 'Top Donantes', icon: PeopleIcon },
  { id: 'gastos', label: 'Gastos por Categoría', icon: ReceiptLongIcon },
  { id: 'missing-payment', label: 'Sin Pago por Mes', icon: TrendingUpIcon }
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const FinancialReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('balance');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para cada reporte
  const [balanceReport, setBalanceReport] = useState<any>(null);
  const [morososReport, setMorososReport] = useState<any>(null);
  const [ingresosGastosReport, setIngresosGastosReport] = useState<any>(null);
  const [donantesReport, setDonantesReport] = useState<any>(null);
  const [gastosReport, setGastosReport] = useState<any>(null);
  
  // Estado de carga por tab
  const [loadingTabs, setLoadingTabs] = useState<Record<string, boolean>>({
    balance: false,
    morosos: false,
    ingresos: false,
    donantes: false,
    gastos: false
  });

  const { control, handleSubmit, watch, setValue } = useForm<FilterFormData>({
    defaultValues: {
      year: dayjs().year(),
      months: 2,
      grade: '',
      school_year_id: ''
    }
  });

  const watchYear = watch('year');
  const watchMonths = watch('months');
  const watchGrade = watch('grade');

  // Cargar reporte según la pestaña activa
  useEffect(() => {
    loadCurrentTabReport();
  }, [activeTab, watchYear, watchMonths, watchGrade]);

  const loadCurrentTabReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      switch (activeTab) {
        case 'balance':
          await loadBalanceReport();
          break;
        case 'morosos':
          await loadMorososReport();
          break;
        case 'ingresos':
          await loadIngresosGastosReport();
          break;
        case 'donantes':
          await loadDonantesReport();
          break;
        case 'gastos':
          await loadGastosReport();
          break;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Error al cargar el reporte`);
    } finally {
      setLoading(false);
    }
  };

  const loadBalanceReport = async () => {
    setLoadingTabs(prev => ({ ...prev, balance: true }));
    try {
      const response = await reportService.getMonthlyBalanceAfterTeacherPayments({ 
        year: watchYear 
      });
      setBalanceReport(response.data);
    } finally {
      setLoadingTabs(prev => ({ ...prev, balance: false }));
    }
  };

  const loadMorososReport = async () => {
    setLoadingTabs(prev => ({ ...prev, morosos: true }));
    try {
      const response = await reportService.getStudentsWithoutPayment({
        months: watchMonths,
        grade: watchGrade || undefined
      });
      setMorososReport(response.data);
    } finally {
      setLoadingTabs(prev => ({ ...prev, morosos: false }));
    }
  };

  const loadIngresosGastosReport = async () => {
    setLoadingTabs(prev => ({ ...prev, ingresos: true }));
    try {
      const response = await reportService.getIncomeVsExpenses({ year: watchYear });
      setIngresosGastosReport(response.data);
    } finally {
      setLoadingTabs(prev => ({ ...prev, ingresos: false }));
    }
  };

  const loadDonantesReport = async () => {
    setLoadingTabs(prev => ({ ...prev, donantes: true }));
    try {
      const response = await reportService.getTopDonors({ year: watchYear });
      setDonantesReport(response.data);
    } finally {
      setLoadingTabs(prev => ({ ...prev, donantes: false }));
    }
  };

  const loadGastosReport = async () => {
    setLoadingTabs(prev => ({ ...prev, gastos: true }));
    try {
      const response = await reportService.getExpensesByCategory({ year: watchYear });
      setGastosReport(response.data);
    } finally {
      setLoadingTabs(prev => ({ ...prev, gastos: false }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleMonthsChange = (value: number) => {
    setValue('months', value);
  };

  const handleGradeChange = (value: string) => {
    setValue('grade', value);
  };

  const handleYearChange = (value: number) => {
    setValue('year', value);
  };

  const currentLoading = loadingTabs[activeTab] || loading;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Reportes Financieros
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {reportTabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={tab.label}
              icon={<tab.icon />}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        {currentLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        )}

        {/* 👈 USAR EL NUEVO COMPONENTE */}
        {activeTab === 'balance' && !currentLoading && balanceReport && (
          <BalanceReportContent report={balanceReport} year={watchYear} />
        )}

        {/* Otros reportes - se mantienen igual */}
        {activeTab === 'morosos' && !currentLoading && morososReport && (
          <MorososReportContent 
            report={morososReport} 
            months={watchMonths} 
            grade={watchGrade}
            onMonthsChange={handleMonthsChange}
            onGradeChange={handleGradeChange}
            onRefresh={loadMorososReport}
          />
        )}

        {activeTab === 'ingresos' && !currentLoading && ingresosGastosReport && (
          <IngresosGastosReportContent report={ingresosGastosReport} year={watchYear} />
        )}

        {activeTab === 'donantes' && !currentLoading && donantesReport && (
          <DonantesReportContent report={donantesReport} year={watchYear} />
        )}

        {activeTab === 'gastos' && !currentLoading && gastosReport && (
          <GastosReportContent report={gastosReport} year={watchYear} />
        )}

        {activeTab === 'missing-payment' && !currentLoading && (
          <MissingPaymentReport />
        )}
      </Box>
    </Box>
  );
};

// ==================== COMPONENTES DE CADA REPORTE ====================

interface MorososReportContentProps {
  report: any;
  months: number;
  grade: string;
  onMonthsChange: (value: number) => void;
  onGradeChange: (value: string) => void;
  onRefresh: () => void;
}

const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'success';
    }
  };

  const getRiskLabel = (risk: string) => {
    switch (risk) {
      case 'critical': return 'Crítico';
      case 'high': return 'Alto';
      case 'medium': return 'Medio';
      default: return 'Bajo';
    }
  };
const MorososReportContent: React.FC<MorososReportContentProps> = ({ 
  report, 
  months, 
  grade, 
  onMonthsChange, 
  onGradeChange, 
  onRefresh 
}) => {
  const grades = ['1A', '1B', '2', '3', '4', '5', '6&7', '8&9', '10', 'Prescolar'];

  const handleExportExcel = async () => {
    try {
        const response = await reportService.exportStudentsWithoutPaymentExcel({
            months,
            grade
        });
        
        // Crear URL del blob
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Estudiantes_morosos_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        alert('Error al exportar a Excel');
    }
};

const handleExportPDF = async () => {
    try {
        const response = await reportService.exportStudentsWithoutPaymentPDF({
            months,
            grade
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Estudiantes_morosos_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        alert('Error al exportar a PDF');
    }
};

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Estudiantes con Pagos Atrasados
            </Typography>

            {/* Filtros */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TextField
                  type="number"
                  fullWidth
                  label="Meses sin pagar"
                  size="small"
                  value={months}
                  onChange={(e) => onMonthsChange(parseInt(e.target.value) || 2)}
                  inputProps={{ min: 1, max: 12 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grado</InputLabel>
                  <Select
                    value={grade}
                    label="Grado"
                    onChange={(e) => onGradeChange(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {grades.map((g) => (
                      <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={onRefresh}
                >
                  Actualizar
                </Button>
              </Grid>
            </Grid>

            {/* Resumen */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Estudiantes Afectados
                    </Typography>
                    <Typography variant="h5">
                      {report.summary.total_students}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Deuda Total Estimada
                    </Typography>
                    <Typography variant="h5" color="error.main">
                      {formatCurrency(report.summary.total_debt)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Promedio Meses Sin Pago
                    </Typography>
                    <Typography variant="h5">
                      {report.summary.average_months_without}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Exportar a:
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box display="flex" gap={1}>
                          <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ExcelIcon />}
                              onClick={handleExportExcel}
                          >
                              Excel
                          </Button>
                          <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PdfIcon />}
                              onClick={handleExportPDF}
                          >
                              PDF
                          </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabla de estudiantes */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>Estudiante</TableCell>
                        <TableCell>Grado</TableCell>
                        <TableCell>Meses Sin Pago</TableCell>
                        <TableCell>Meses Faltantes</TableCell>
                        <TableCell>Último Pago</TableCell>
                        <TableCell>Riesgo</TableCell>
                        <TableCell>Acciones</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {report.students.map((student: any) => (
                        <TableRow key={student.id}>
                            <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                    {student.full_name}
                                </Typography>
                                {student.sponsor && (
                                    <Typography variant="caption" color="text.secondary">
                                        Patrocinador: {student.sponsor}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell>{student.grade}</TableCell>
                            <TableCell>
                                <Tooltip title={`${student.months_without_payment} meses sin pagar`}>
                                    <Box sx={{ width: 80 }}>
                                        <Typography variant="body2" align="center">
                                            {student.months_without_payment} meses
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min(student.months_without_payment * 10, 100)}
                                            color={getRiskColor(student.risk_level)}
                                            sx={{ height: 4, borderRadius: 2 }}
                                        />
                                    </Box>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip 
                                  title={student.pending_months?.map((m: any) => `${m.month} ${m.year}`).join('\n') || 'Ninguno'}
                                  arrow
                              >
                                  <Typography variant="caption">
                                      {student.pending_months_count || 0} meses
                                      {student.pending_months?.length > 0 && (
                                          <span style={{ display: 'block', fontSize: '8px', color: '#666' }}>
                                              {student.pending_months?.slice(0, 3).map((m: any) => `${m.month} ${m.year}`).join(', ')}
                                              {student.pending_months?.length > 3 && ` +${student.pending_months.length - 3} más`}
                                          </span>
                                      )}
                                  </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                                {student.last_payment ? (
                                    <Typography variant="caption">
                                        {student.last_payment.month} {student.last_payment.year}
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="error">
                                        Nunca pagó
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={getRiskLabel(student.risk_level)}
                                    color={getRiskColor(student.risk_level)}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => window.open(`/students/${student.id}`, '_blank')}
                                >
                                    Ver
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const IngresosGastosReportContent: React.FC<{ report: any; year: number }> = ({ report, year }) => {
  const data = report?.monthly_data || [];
  
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Ingresos vs Gastos - {year}
            </Typography>

            {/* Gráfico */}
            <Box sx={{ height: 400, mb: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month_name" />
                  <YAxis tickFormatter={(v) => formatCurrency(v as number)} />
                  <RechartsTooltip formatter={(v) => formatCurrency(v as number)} />
                  <Legend />
                  <Bar dataKey="ingresos" fill="#4caf50" name="Ingresos" />
                  <Bar dataKey="gastos" fill="#f44336" name="Gastos" />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Tabla resumen */}
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Total Ingresos</strong></TableCell>
                    <TableCell align="right">{formatCurrency(report.summary.total_ingresos)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Total Gastos</strong></TableCell>
                    <TableCell align="right">{formatCurrency(report.summary.total_gastos)}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: report.summary.balance >= 0 ? 'success.50' : 'error.50' }}>
                    <TableCell><strong>Balance Neto</strong></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(report.summary.balance)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const DonantesReportContent: React.FC<{ report: any; year: number }> = ({ report, year }) => {
  const topDonors = report?.top_donors || [];
  
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Donantes - {year}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>#</TableCell>
                    <TableCell>Donante</TableCell>
                    <TableCell align="right">Total Donado</TableCell>
                    <TableCell align="center">Donaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topDonors.map((donor: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {donor.donor_name}
                        </Typography>
                        {donor.russian_name && (
                          <Typography variant="caption" color="text.secondary">
                            {donor.russian_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(donor.total)}
                      </TableCell>
                      <TableCell align="center">{donor.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      
      
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Distribución de Donaciones
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={topDonors.slice(0, 5)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => {
                      const percent = entry.percent;
                      if (!percent || percent === 0) return entry.name;
                      return `${entry.name}: ${(percent * 100).toFixed(0)}%`;
                    }}
                    outerRadius={80}
                    dataKey="total"
                    nameKey="donor_name"
                  >
                    {topDonors.slice(0, 5).map((_: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(v) => formatCurrency(v as number)} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const GastosReportContent: React.FC<{ report: any; year: number }> = ({ report, year }) => {
  const categories = report?.categories || [];
  
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Gastos por Categoría - {year}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Categoría</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell align="center">%</TableCell>
                    <TableCell align="center">Transacciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((cat: any) => (
                    <TableRow key={cat.category}>
                      <TableCell>{cat.category}</TableCell>
                      <TableCell align="right">{formatCurrency(cat.total)}</TableCell>
                      <TableCell align="center">{cat.percentage}%</TableCell>
                      <TableCell align="center">{cat.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Distribución de Gastos
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => {
                        const percent = entry.percent;
                        if (!percent || percent === 0) return entry.name;
                        return `${entry.name}: ${(percent * 100).toFixed(0)}%`;
                    }}
                    outerRadius={80}
                    dataKey="total"
                    nameKey="category"
                  >
                    {categories.map((_: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(v) => formatCurrency(v as number)} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};


export default FinancialReports;